import type { AgentTaskList } from "@opencloud/agent-protocol";
import { latestActiveRoot } from "./presentation.js";
import type { ConversationRun, ChatSnapshot as ConversationSnapshot, ConversationTaskList, RunStatus } from "./types.js";

export type TaskListLifecycle = "active" | "completed" | "failed" | "cancelled";

export function taskListLifecycle(run: ConversationRun): TaskListLifecycle {
  if (run.status === "failed" || run.status === "cancelled") return run.status;
  return run.phase === "finished" || run.status === "completed" || run.status === "idle"
    ? "completed" : "active";
}

export function taskListPresentation(taskList: AgentTaskList, lifecycle: TaskListLifecycle, runStatus?: RunStatus) {
  const completed = taskList.items.filter((item) => item.status === "completed").length;
  const active = taskList.items.find((item) => item.status === "in_progress");
  const allDone = taskList.items.length > 0 && completed === taskList.items.length;
  const waiting = runStatus === "paused" || runStatus === "waiting_for_user";
  const moving = lifecycle === "active" && Boolean(active) && !waiting && !["cancelling", "pausing", "queued"].includes(runStatus ?? "");
  const status = lifecycle === "failed" ? "Failed"
    : lifecycle === "cancelled" ? "Canceled"
    : lifecycle === "completed" ? allDone ? "Completed" : "Unfinished"
    : runStatus === "paused" ? "Paused"
    : runStatus === "waiting_for_user" ? "Needs reply"
    : runStatus === "pausing" ? "Pausing"
    : runStatus === "cancelling" ? "Stopping"
    : runStatus === "queued" ? "Queued"
    : runStatus === "resuming" ? "Resuming"
    : allDone ? "Finishing" : active ? "In progress" : "Pending";
  const title = lifecycle === "failed" ? "Plan interrupted"
    : lifecycle === "cancelled" ? "Plan stopped"
    : lifecycle === "completed" ? allDone ? "Plan complete" : "Plan finished with work remaining"
    : waiting ? status : active?.label ?? (allDone ? "Tasks complete · finishing up" : "Ready to start");
  const tone = lifecycle === "failed" ? "danger"
    : lifecycle === "cancelled" || waiting ? "neutral"
    : lifecycle === "completed" ? allDone ? "success" : "warning" : "info";
  return { completed, allDone, moving, status, title, tone, count: `${completed} of ${taskList.items.length} completed` } as const;
}

/** Prefer the newest retained native revision; never borrow another run's plan. */
export function conversationTaskLists(snapshot: ConversationSnapshot): ConversationTaskList[] {
  const byRun = new Map<string, ConversationTaskList>();
  const records = [...(snapshot.taskLists ?? []), ...(snapshot.uiMessages ?? []).flatMap((message) =>
    message.parts.flatMap((part) => part.type === "data-plan" ? [{
      runId: part.data.runId, occurredAt: part.data.occurredAt,
      updatedAt: part.data.updatedAt, taskList: part.data.taskList,
    }] : []))];
  for (const record of records) {
    const previous = byRun.get(record.runId);
    if (!previous || record.taskList.revision >= previous.taskList.revision) byRun.set(record.runId, record);
  }
  return [...byRun.values()];
}

export function latestConversationPlan(snapshot: ConversationSnapshot) {
  const run = latestActiveRoot(snapshot.runs) ?? [...snapshot.runs].reverse().find((item) => item.parentRunId === null);
  const record = run ? conversationTaskLists(snapshot).find((item) => item.runId === run.id) : undefined;
  return run && record ? { ...record, run, lifecycle: taskListLifecycle(run) } : null;
}

export function isFinishedTaskList(taskList: AgentTaskList, run: ConversationRun | undefined) {
  return Boolean(run && taskListLifecycle(run) === "completed" && taskList.items.length > 0
    && taskList.items.every((item) => item.status === "completed"));
}
